import { html, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { ChartType } from '../../../../ui/gzGeneralChart/types';
import { CHART_AREA_OPTIONS } from '../../../../ui/gzGeneralChart/types/chart.config';
import { translateFn } from '../../../../utils/translator.helper';
import { GzGeneralChartWebComponent } from '../../../../ui/gzGeneralChart';
import { RM_TIME_RANGE_30_DAYS, RM_TIME_RANGE_7_DAYS, RM_TIME_RANGE_90_DAYS } from '../types';
import { RmScoreOverTimePayload, ScoreOverTime, ScoreOverTimeItem } from '@nirvana/shared/dtos/types/widgets/common';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { ApexOptions } from '../../../../charts/apexchart';
import { translateDate } from '../../../../utils/riskManagement.utils';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-score-over-time-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-rm-score-over-time-widget')
export class GzRmScoreOverTimeWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmScoreOverTimePayload;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  @query('#rmScoreOverTime')
  private chartComponent!: GzGeneralChartWebComponent;

  @query('#dropdownTimerangeSelection')
  private timerangeSelection!: GzDropdownWebComponent;

  @property({ type: Array, attribute: true, reflect: true })
  resizeBreakingPoints: number[] = [];

  private fillColors = ['#F0341B66'];
  private lineColor = ['#f0341b'];

  protected renderLoader(): TemplateResult {
    return html` <gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 4)"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    let dropdownValue: number = RM_TIME_RANGE_7_DAYS;
    if (this.timerangeSelection && this.timerangeSelection.value) {
      dropdownValue = parseInt(this.timerangeSelection.value.toString(), 10);
    }

    const { series, categories, noScoreSeriesIndexes } = this.getDataSeriesAndCategoriesForChart(dropdownValue);

    return html` <gz-dropdown
        id="dropdownTimerangeSelection"
        class="d-flex justify-end pr-1"
        .options=${this.getTimerangeOptions()}
        .value=${dropdownValue}
        @optionSelected=${this.handleTimerangeChanged}
        slot="headerExtraContent"
      ></gz-dropdown>

      <gz-general-chart
        id="rmScoreOverTime"
        .type="${ChartType.CUSTOM}"
        .series="${series}"
        .categories="${categories}"
        .customConfig="${this.getOptionsForChart(categories, noScoreSeriesIndexes)}"
        .emptyMessage="${''}"
        .width="${this.contentWidth}"
        .height="${this.calculateHeight()}"
      >
      </gz-general-chart>`;
  }

  private getTimerangeOptions() {
    return [
      {
        id: 1,
        title: translateFn('Last 7 days'),
        value: RM_TIME_RANGE_7_DAYS,
      },
      {
        id: 2,
        title: translateFn('Last 30 days'),
        value: RM_TIME_RANGE_30_DAYS,
      },
      {
        id: 3,
        title: translateFn('Last 90 days'),
        value: RM_TIME_RANGE_90_DAYS,
      },
    ];
  }

  private calculateHeight() {
    return (this.contentHeight = this.contentHeight - 32);
  }

  private getOptionsForChart(categoriesData, noScoreSeriesIndexes): ApexOptions {
    const discreteMarkers: any[] = [];
    noScoreSeriesIndexes.forEach(item => {
      discreteMarkers.push({
        seriesIndex: 0,
        dataPointIndex: item,
        size: 0,
      });
    });

    const chartOptions = CHART_AREA_OPTIONS;

    chartOptions.colors = this.lineColor;
    chartOptions.fill = {
      ...chartOptions.fill,
      colors: this.fillColors,
      opacity: 1,
    };
    chartOptions.yaxis = {
      ...chartOptions.yaxis,
      tickAmount: this.isSmallHeight() ? 5 : 10,
      min: 0,
      max: 100,
      labels: {
        style: {
          fontFamily: 'var(--font-family-secondary)',
        },
      },
    };
    chartOptions.markers = {
      size: 3,
      hover: {
        sizeOffset: 2,
      },
      discrete: discreteMarkers,
    };
    chartOptions.legend = {
      show: false,
    };
    chartOptions.tooltip = {
      ...chartOptions.tooltip,
      custom: ({ series, seriesIndex, dataPointIndex, w }): string => {
        if (series[seriesIndex][dataPointIndex] === -1) {
          return '';
        }

        return `<div class="tooltip" style="display: flex; flex-direction: column; min-width: 200px; max-width: 230px;" part="tooltip">
                <span style="align-self: flex-start; color: var(--chart-300-icon-buttons);" part="tooltip-title" >${translateDate(
                  categoriesData[dataPointIndex],
                  3
                )} </span>
                  <div class="element-container" style="padding-top: var(--spacer); width: 100%; align-self: flex-start; display: flex; align-items: center;">
                    <span class='tooltip--bullet' style="background-color: ${
                      w.globals.colors[seriesIndex]
                    }; margin-right: calc(var(--spacer) / 4)"></span>
                    <div class="label-container" style="display: flex; justify-content: space-between; flex: 1; padding-left: 4px; width: calc(100% - 24px);">
                        <span class='tooltip--label flex-1' style="color: var(--body-text-arrows-400); flex: 1;" part="tooltip-text">${translateFn(
                          'Company risk score'
                        )}: </span>
                    <span class='tooltip--value' style='float: right;' part="tooltip-value">${
                      series[seriesIndex][dataPointIndex]
                    }</span>
</div>
                  </div>
              </div>
          `;
      },
    };

    chartOptions.xaxis = {
      ...chartOptions.xaxis,
      type: 'category',
      categories: categoriesData,
      labels: {
        show: true,
        rotate: this.isSmallWidgetSize() ? -45 : 0,
        rotateAlways: true,
        hideOverlappingLabels: true,
        style: {
          fontFamily: 'var(--font-family-secondary)',
        },
        formatter: (value, timestamp, opts) => {
          if (typeof value !== 'string') {
            return value;
          }

          return translateDate(value, 2);
        },
      },
      tickAmount: 15,
      tooltip: {
        enabled: false,
      },
    };

    return chartOptions;
  }

  private isSmallWidgetSize(): boolean {
    return !this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0];
  }

  private isSmallHeight(): boolean {
    return this.contentHeight < 280;
  }

  protected handleTimerangeChanged(event: CustomEvent) {
    const { series, categories, noScoreSeriesIndexes } = this.getDataSeriesAndCategoriesForChart(
      event.detail.newValue.value
    );

    this.chartComponent.series = series;
    this.chartComponent.categories = categories;

    // @ts-ignore
    this.chartComponent.customConfig = this.getOptionsForChart(categories, noScoreSeriesIndexes);
  }

  getDataSeriesAndCategoriesForChart(timeRange: number) {
    const fullDataSeries: number[] = [];
    const fullCategories: any[] = [];
    let noScoreSeriesIndexes: number[] = [];

    this.payload?.scoreOverTime?.items?.forEach((item: ScoreOverTimeItem, index) => {
      if (item.score === undefined) {
        fullDataSeries.push(-1);
      } else {
        fullDataSeries.push(item.score);
      }

      fullCategories.push(item.date);
    });

    const chartData: any = {
      name: translateFn('Score over time'),
      data: [],
    };
    let categories: any = [];

    switch (timeRange) {
      case RM_TIME_RANGE_7_DAYS:
        const sevenDaysData = fullDataSeries.slice(fullDataSeries.length - RM_TIME_RANGE_7_DAYS, fullDataSeries.length);
        noScoreSeriesIndexes = this.getNoScoreIndexes(sevenDaysData);

        chartData.data = sevenDaysData;
        categories = fullCategories.slice(fullCategories.length - RM_TIME_RANGE_7_DAYS, fullCategories.length);
        break;
      case RM_TIME_RANGE_30_DAYS:
        const thirtyDaysData = fullDataSeries.slice(
          fullDataSeries.length - RM_TIME_RANGE_30_DAYS,
          fullDataSeries.length
        );
        noScoreSeriesIndexes = this.getNoScoreIndexes(thirtyDaysData);

        chartData.data = thirtyDaysData;
        categories = fullCategories.slice(fullCategories.length - RM_TIME_RANGE_30_DAYS, fullCategories.length);
        break;
      case RM_TIME_RANGE_90_DAYS:
        const ninetyDaysData = fullDataSeries.slice(
          fullDataSeries.length - RM_TIME_RANGE_90_DAYS,
          fullDataSeries.length
        );
        noScoreSeriesIndexes = this.getNoScoreIndexes(ninetyDaysData);

        chartData.data = ninetyDaysData;
        categories = fullCategories.slice(fullCategories.length - RM_TIME_RANGE_90_DAYS, fullCategories.length);
        break;
      default:
        break;
    }

    return {
      series: [chartData],
      categories,
      noScoreSeriesIndexes,
    };
  }

  protected getNoScoreIndexes(data) {
    const noScoreSeriesIndexes: number[] = [];

    data.forEach((item: number, index) => {
      if (item === -1) {
        noScoreSeriesIndexes.push(index);
      }
    });

    return noScoreSeriesIndexes;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-score-over-time-widget': GzRmScoreOverTimeWidgetWebComponent;
  }
}
